// pages/admin/StudentList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  MdSearch, MdPerson, MdPhone, MdEmail, MdDelete,
  MdSchool, MdVisibility, MdClose, MdStar,
} from 'react-icons/md';

export default function StudentsList() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  // Detail modal
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);

  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (subFilter) params.has_subscription = subFilter;

      const res = await axios.get(
        `${base_url}/api/v1/users/admin/list`,
        { params }
      );
      if (res.data.success) {
        setUsers(res.data.data?.data || []);
        setPagination(
          res.data.data?.pagination || { total: 0, totalPages: 0 }
        );
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [base_url, page, search, subFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? All their saved colleges and history will be removed.`)) return;
    setActionLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.delete(`${base_url}/api/v1/users/admin/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setPagination((p) => ({ ...p, total: p.total - 1 }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const handleToggleStatus = async (id, current) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === id ? { ...u, status: !current } : u))
    );
    try {
      await axios.patch(
        `${base_url}/api/v1/users/admin/${id}/status`,
        { status: !current }
      );
    } catch (err) {
      setUsers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status: current } : u))
      );
      alert(err?.response?.data?.message || 'Failed');
    }
  };

  const openDetails = async (id) => {
    setShowModal(true);
    setModalLoading(true);
    setModalData(null);
    try {
      const res = await axios.get(
        `${base_url}/api/v1/users/admin/${id}`
      );
      if (res.data.success) setModalData(res.data.data);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to load');
    } finally {
      setModalLoading(false);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '-';

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          <div className="card-header bg-white">
            <h4 className="fw-bold mb-3">
              <MdPerson className="me-2 text-primary" />
              Registered Students
              <span className="badge bg-primary ms-2">{pagination.total}</span>
            </h4>

            <div className="row g-3">
              <div className="col-md-7">
                <div className="input-group">
                  <span className="input-group-text"><MdSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, email or phone..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                </div>
              </div>

              <div className="col-md-5">
                <select
                  className="form-select"
                  value={subFilter}
                  onChange={(e) => { setSubFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Users</option>
                  <option value="yes">With Subscription</option>
                  <option value="no">Without Subscription (Free)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Student</th>
                    <th>Contact</th>
                    <th>NEET</th>
                    <th>State / Course</th>
                    <th>Plan</th>
                    <th>Saved</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th style={{ width: 130 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(10)].map((__, j) => (
                          <td key={j}><Skeleton /></td>
                        ))}
                      </tr>
                    ))
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-5 text-muted">
                        <MdPerson size={40} />
                        <div className="mt-2">No students registered yet</div>
                      </td>
                    </tr>
                  ) : (
                    users.map((u, idx) => (
                      <tr key={u.id}>
                        <td>{(page - 1) * limit + idx + 1}</td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: '#01457B',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {(u.name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <strong>{u.name}</strong>
                              <div className="small text-muted">ID #{u.id}</div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="small">
                            <MdPhone /> {u.phone}
                          </div>
                          <div className="small text-muted text-truncate" style={{ maxWidth: 200 }}>
                            <MdEmail /> {u.email}
                          </div>
                        </td>

                        <td>
                          {u.neet_rank && (
                            <div className="small">
                              <strong>Rank:</strong> {u.neet_rank}
                            </div>
                          )}
                          {u.neet_score && (
                            <div className="small">
                              <strong>Score:</strong> {u.neet_score}
                            </div>
                          )}
                          {!u.neet_rank && !u.neet_score && (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        <td>
                          <div className="small">{u.state || '-'}</div>
                          <div className="small text-muted">{u.course || 'MBBS'}</div>
                        </td>

                        <td>
                          {u.active_plan ? (
                            <span className="badge bg-success text-uppercase">
                              {u.active_plan}
                            </span>
                          ) : (
                            <span className="badge bg-light text-dark">Free</span>
                          )}
                        </td>

                        <td>
                          <span className="badge bg-info text-light">
                            {u.saved_count ?? 0}
                          </span>
                        </td>

                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!u.status}
                              onChange={() => handleToggleStatus(u.id, u.status)}
                              style={{ cursor: 'pointer' }}
                              title={u.status ? 'Active' : 'Disabled'}
                            />
                          </div>
                        </td>

                        <td className="small">{formatDate(u.created_at)}</td>

                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDetails(u.id)}
                              title="View details"
                            >
                              <MdVisibility />
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              disabled={actionLoading[u.id]}
                              onClick={() => handleDelete(u.id, u.name)}
                              title="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)} -{' '}
                  {Math.min(page * limit, pagination.total)} of {pagination.total}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{page}</strong> of{' '}
                    <strong>{pagination.totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalData?.user?.name || 'Student Details'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                {modalLoading && (
                  <div className="text-center py-4"><Skeleton count={6} /></div>
                )}

                {!modalLoading && modalData && (
                  <>
                    {/* User Info */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6"><strong>ID:</strong> #{modalData.user.id}</div>
                      <div className="col-md-6"><strong>Name:</strong> {modalData.user.name}</div>
                      <div className="col-md-6"><strong>Email:</strong> {modalData.user.email}</div>
                      <div className="col-md-6"><strong>Phone:</strong> {modalData.user.phone}</div>
                      <div className="col-md-6"><strong>NEET Rank:</strong> {modalData.user.neet_rank || '-'}</div>
                      <div className="col-md-6"><strong>NEET Score:</strong> {modalData.user.neet_score || '-'}</div>
                      <div className="col-md-6"><strong>State:</strong> {modalData.user.state || '-'}</div>
                      <div className="col-md-6"><strong>Category:</strong> {modalData.user.category || '-'}</div>
                      <div className="col-md-6"><strong>Course:</strong> {modalData.user.course || 'MBBS'}</div>
                      <div className="col-md-6">
                        <strong>Status:</strong>{' '}
                        {modalData.user.status ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-danger">Disabled</span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <strong>Joined:</strong> {formatDate(modalData.user.created_at)}
                      </div>
                    </div>

                    {/* Subscriptions */}
                    <h6 className="fw-bold mt-4 mb-2">
                      <MdStar className="text-warning" /> Subscription History ({modalData.subscriptions.length})
                    </h6>
                    {modalData.subscriptions.length === 0 ? (
                      <p className="text-muted small mb-4">No subscriptions yet (Free plan).</p>
                    ) : (
                      <div className="table-responsive mb-4">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Plan</th>
                              <th>Price</th>
                              <th>Status</th>
                              <th>Payment</th>
                              <th>Valid Until</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalData.subscriptions.map((s) => (
                              <tr key={s.id}>
                                <td className="text-capitalize">{s.plan_name}</td>
                                <td>₹{s.price}</td>
                                <td>
                                  <span className={`badge bg-${s.status === 'active' ? 'success' : 'secondary'}`}>
                                    {s.status}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge bg-${s.payment_status === 'success' ? 'success' : 'warning'}`}>
                                    {s.payment_status || 'pending'}
                                  </span>
                                </td>
                                <td>{formatDate(s.expires_at)}</td>
                                <td>{formatDate(s.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Saved Colleges */}
                    <h6 className="fw-bold mt-4 mb-2">
                      <MdSchool className="text-primary" /> Saved Colleges ({modalData.saved_colleges.length})
                    </h6>
                    {modalData.saved_colleges.length === 0 ? (
                      <p className="text-muted small mb-4">No saved colleges.</p>
                    ) : (
                      <div className="list-group mb-4">
                        {modalData.saved_colleges.map((sc) => (
                          <div
                            key={sc.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <span className="badge bg-light text-dark me-2 text-capitalize">
                                {sc.college_type}
                              </span>
                              <span className="small">{sc.college_slug || `ID #${sc.college_id}`}</span>
                            </div>
                            <span className="text-muted small">
                              {formatDate(sc.created_at)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Leads */}
                    <h6 className="fw-bold mt-4 mb-2">
                      Counselling Leads ({modalData.leads.length})
                    </h6>
                    {modalData.leads.length === 0 ? (
                      <p className="text-muted small mb-0">No counselling leads.</p>
                    ) : (
                      <div className="list-group">
                        {modalData.leads.map((l) => (
                          <div key={l.id} className="list-group-item">
                            <div className="d-flex justify-content-between">
                              <strong>{l.college_name || l.source || 'General'}</strong>
                              <span className={`badge bg-${l.status === 'new' ? 'primary' : l.status === 'contacted' ? 'warning' : 'success'}`}>
                                {l.status || 'new'}
                              </span>
                            </div>
                            <div className="small text-muted">
                              {formatDate(l.created_at)} · {l.phone}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}